import { SCMI_BRAND, SCMI_CONTENT } from './templateConfig';

async function loadImageAsDataUrl(url) {
  const response = await fetch(url, { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error(`Image not found (${response.status}): ${url}`);
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function measureImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

async function svgToPngDataUrl(url) {
  const response = await fetch(url, { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error(`SVG not found (${response.status}): ${url}`);
  }
  const svgText = await response.text();
  const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const naturalWidth = img.naturalWidth || img.width || 320;
      const naturalHeight = img.naturalHeight || img.height || 96;
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = naturalWidth * scale;
      canvas.height = naturalHeight * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(objectUrl);
      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: naturalWidth,
        height: naturalHeight,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to render SVG: ${url}`));
    };
    img.src = objectUrl;
  });
}

let cachedLogoAsset = null;

function getLogoDisplaySizeMm(aspectRatio) {
  const maxW = SCMI_BRAND.logoMaxWidthMm;
  const maxH = SCMI_BRAND.logoMaxHeightMm;

  let widthMm = maxW;
  let heightMm = widthMm * aspectRatio;

  if (heightMm > maxH) {
    heightMm = maxH;
    widthMm = heightMm / aspectRatio;
  }

  return { widthMm, heightMm };
}

/**
 * Load Selkirk logo with natural aspect ratio for PDF placement.
 * @returns {Promise<{ dataUrl: string, widthMm: number, heightMm: number } | null>}
 */
export async function loadSelkirkLogoAsset() {
  if (cachedLogoAsset) return cachedLogoAsset;

  const candidates = [SCMI_BRAND.logoUrl, SCMI_BRAND.logoFallbackUrl].filter(Boolean);
  let lastError = null;

  for (const url of candidates) {
    try {
      let dataUrl;
      let width;
      let height;

      if (url.endsWith('.svg')) {
        const svgResult = await svgToPngDataUrl(url);
        dataUrl = svgResult.dataUrl;
        width = svgResult.width;
        height = svgResult.height;
      } else {
        dataUrl = await loadImageAsDataUrl(url);
        const dims = await measureImage(dataUrl);
        width = dims.width;
        height = dims.height;
      }

      if (!width || !height) {
        throw new Error(`Could not determine logo dimensions for ${url}`);
      }

      const aspectRatio = height / width;
      const { widthMm, heightMm } = getLogoDisplaySizeMm(aspectRatio);

      cachedLogoAsset = { dataUrl, widthMm, heightMm };
      return cachedLogoAsset;
    } catch (error) {
      lastError = error;
    }
  }

  console.warn('Selkirk logo unavailable for PDF:', lastError?.message);
  return null;
}

/** @deprecated Use loadSelkirkLogoAsset */
export async function loadSelkirkLogoDataUrl() {
  const asset = await loadSelkirkLogoAsset();
  return asset?.dataUrl ?? null;
}

/**
 * Draw SCMI report page chrome: logo (top-right), single template gold rule, page title above rule, page number badge.
 */
export function applyReportPageChrome(pdf, pageNumber, logoAsset, options = {}) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = options.marginMm ?? SCMI_BRAND.marginMm;
  const [r, g, b] = SCMI_BRAND.goldRgb;
  const templateLineY = SCMI_BRAND.templateLineYMm ?? SCMI_CONTENT.templateLineYMm;

  if (logoAsset?.dataUrl) {
    pdf.addImage(
      logoAsset.dataUrl,
      'PNG',
      pageWidth - margin - logoAsset.widthMm,
      SCMI_BRAND.logoTopMm,
      logoAsset.widthMm,
      logoAsset.heightMm
    );
  }

  const pageTitle = options.pageTitle?.trim();
  if (pageTitle) {
    pdf.setFontSize(SCMI_CONTENT.pageTitleFontSize);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(17, 24, 39);
    pdf.text(pageTitle, margin, SCMI_CONTENT.pageTitleYMm);
  }

  const pageSubtitle = options.pageSubtitle?.trim();
  if (pageSubtitle) {
    pdf.setFontSize(SCMI_CONTENT.pageSubtitleFontSize);
    pdf.setFont(undefined, 'normal');
    pdf.setTextColor(107, 114, 128);
    pdf.text(pageSubtitle, margin, SCMI_CONTENT.pageSubtitleYMm);
  }

  pdf.setDrawColor(r, g, b);
  pdf.setLineWidth(SCMI_BRAND.headerLineWidthMm);
  pdf.line(margin, templateLineY, pageWidth - margin, templateLineY);

  const boxW = SCMI_BRAND.footerBoxWidthMm;
  const boxH = SCMI_BRAND.footerBoxHeightMm;
  const boxX = pageWidth - boxW;
  const boxY = pageHeight - boxH;

  pdf.setFillColor(r, g, b);
  if (typeof pdf.roundedRect === 'function') {
    pdf.roundedRect(boxX, boxY, boxW, boxH, 2, 2, 'F');
  } else {
    pdf.rect(boxX, boxY, boxW, boxH, 'F');
  }

  pdf.setFont(undefined, 'bold');
  pdf.setFontSize(SCMI_BRAND.pageNumberFontSize);
  pdf.setTextColor(255, 255, 255);
  pdf.text(String(pageNumber), boxX + boxW / 2, boxY + boxH / 2 + 1, {
    align: 'center',
  });
  pdf.setTextColor(0, 0, 0);
}

export function getReportContentLayout(options = {}) {
  const margin = options.marginMm ?? SCMI_BRAND.marginMm;
  return {
    margin,
    contentTopMm: SCMI_BRAND.contentTopMm,
    bottomReserveMm: SCMI_BRAND.footerReserveMm,
  };
}
